/* Phoenix TCG — animations.js
 *
 * anime.js v4 integration. See HANDOVER.md for the Heavy-tier scope.
 * Loaded deferred after anime.umd.min.js in layout/theme.liquid.
 *
 * ----- Design principles -----
 *
 * 1. ADDITIVE animations only — never make element visibility contingent
 *    on script success. Marks are visible at CSS default (opacity 0.40)
 *    from page load; the animation adds polish, never reveals content.
 *
 * 2. SCROLL TRIGGER via native IntersectionObserver (NOT anime.js onScroll).
 *    IntersectionObserver has been stable since 2017, the threshold syntax
 *    is unambiguous, and a known-working example lives in templates/
 *    index.liquid for `.reveal` elements. We use it to dispatch animations
 *    once per card on entry.
 *
 * Phase coverage:
 *   A — Foundation: feature-detect, reduced-motion guard, init dispatch
 *   B — Use-case persona mark entrance animations (additive, IO-triggered)
 *
 * Future phases (C, D, E) append init functions here.
 */
(function () {
  'use strict';

  // ----- Guards -------------------------------------------------------------

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  if (typeof window.anime === 'undefined') {
    if (window.console && console.warn) {
      console.warn('Phoenix: anime.js failed to load — animations disabled.');
    }
    return;
  }

  // IntersectionObserver fallback — for very old browsers, skip silently.
  if (typeof window.IntersectionObserver === 'undefined') return;

  var anime = window.anime;
  var animate = anime.animate;
  var stagger = anime.stagger;

  // ----- Phase B: Use-case persona mark entrance animations -----------------

  // Per-persona tuning. Higher amplitude than the first attempt — scale 0.7
  // and translateY ~20px so the entrance is *visibly* an animation, not just
  // a settle. Each persona's easing curve shapes the personality.
  var PERSONA_CONFIG = {
    newcomer:    { translateY: 22, scale: 0.75, childTranslateY: 10, duration: 900,  ease: 'outElastic(1, 0.6)', staggerFrom: 'first',  staggerStep: 110 },
    casual:      { translateY: 20, scale: 0.85, childTranslateY:  8, duration: 800,  ease: 'outQuad',             staggerFrom: 'center', staggerStep: 90  },
    competitive: { translateY: 24, scale: 0.70, childTranslateY: 12, duration: 600,  ease: 'outExpo',             staggerFrom: 'first',  staggerStep: 70  },
    collector:   { translateY: 20, scale: 0.80, childTranslateY: 10, duration: 1000, ease: 'outCubic',            staggerFrom: 'first',  staggerStep: 120 }
  };

  function playPersonaAnimation(card) {
    var audience = card.dataset.audience;
    var mark = card.querySelector('.uc-mark');
    if (!mark || !PERSONA_CONFIG[audience]) return;

    var config = PERSONA_CONFIG[audience];
    var children = Array.prototype.slice.call(mark.children);

    // Container animation: scale + translate, persona-tuned.
    animate(mark, {
      translateY: [config.translateY, 0],
      scale: [config.scale, 1],
      duration: config.duration,
      ease: config.ease,
      autoplay: true
    });

    // Children stagger — persona narrative via direction + timing.
    if (children.length > 1) {
      animate(children, {
        translateY: [config.childTranslateY, 0],
        duration: 450,
        delay: stagger(config.staggerStep, { start: 200, from: config.staggerFrom }),
        ease: 'outQuad',
        autoplay: true
      });
    }
  }

  function initPersonaMarks() {
    var cards = document.querySelectorAll('.use-cases .card-uc');
    if (!cards.length) return;

    // Native IntersectionObserver. Trigger animation when 15% of the card
    // is in view; unobserve after first trigger so it doesn't replay on
    // re-scroll past.
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        playPersonaAnimation(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -8% 0px' // trigger slightly before the card crosses the bottom edge
    });

    cards.forEach(function (card) { observer.observe(card); });
  }

  // ----- Phase C: Trust bar icon path-drawing -------------------------------

  // The trust bar icons are stroked SVG paths/rects/circles. We use
  // anime.js's classic path-draw technique: set strokeDasharray = totalLength,
  // animate strokeDashoffset from totalLength (invisible) to 0 (fully drawn).
  // Each icon's strokes draw themselves onto the screen in sequence.
  //
  // The existing CSS .reveal-group block-level fade is left untouched —
  // this animation runs IN ADDITION to it, so user sees both: block fades
  // up + icon strokes draw in. If anime.js fails, the CSS reveal still works.
  function initTrustBar() {
    var trustBar = document.querySelector('.trust');
    if (!trustBar) return;

    var blocks = Array.prototype.slice.call(trustBar.querySelectorAll('.block'));
    if (!blocks.length) return;

    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      // Note: NOT calling observer.unobserve here — we want the draw to
      // replay each time the trust bar re-enters view (e.g. user scrolls
      // past, then back). IntersectionObserver fires once per state change,
      // so this gives one replay per re-engagement, not a continuous loop.

      blocks.forEach(function (block, blockIdx) {
        var svg = block.querySelector('svg');
        if (!svg) return;

        // All geometry-bearing SVG children. SVGGeometryElement.getTotalLength()
        // works for these element types in all modern browsers (since ~2019).
        var geom = svg.querySelectorAll('path, rect, circle, line, polygon, polyline');

        geom.forEach(function (el, elIdx) {
          try {
            var length = el.getTotalLength();
            if (!length) return; // zero-length element (rare); skip

            // Set dasharray to lock the draw window; the from-to in animate()
            // will set dashoffset = length synchronously on the first tick.
            el.style.strokeDasharray = length;

            animate(el, {
              strokeDashoffset: [length, 0],
              duration: 800,
              delay: blockIdx * 120 + elIdx * 50,
              ease: 'outCubic',
              autoplay: true,
              onComplete: function () {
                // Clean up the inline dash styles so future CSS rules
                // (e.g. hover stroke changes) aren't constrained.
                el.style.strokeDasharray = '';
                el.style.strokeDashoffset = '';
              }
            });
          } catch (e) {
            // getTotalLength can throw on some pathological SVG shapes.
            // Fail gracefully — the icon stays at its default rendered state.
          }
        });
      });
    }, { threshold: 0.20, rootMargin: '0px 0px -8% 0px' });

    observer.observe(trustBar);
  }

  // ----- Init dispatch ------------------------------------------------------

  function init() {
    initPersonaMarks();
    initTrustBar();
    // Phase D — initScrollReveal();
    // Phase E — initHeroParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
